/**
 * Fill / Choose / Translate Exercise Component
 *
 * Handles three exercise types:
 * - fill_blank: User fills in a missing word
 * - choose_correct: Multiple choice with 4 options
 * - translate: User sees the answer (reveal-based)
 *
 * Extracted from LessonDetailScreen for reuse in QuickPracticeSheet.
 */

import React, { useState, useCallback } from 'react';
import { TouchableOpacity } from 'react-native';
import { Box, VStack, Text } from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import { FS_XS, FS_SM } from '../../theme';
import { useNumberKeyShortcuts } from '../../hooks/useKeyboardShortcut';
import type { ExerciseProgress } from '../../types/lesson';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';

export interface FillOrChooseExerciseProps {
  exerciseId: string;
  type: 'fill_blank' | 'choose_correct' | 'translate';
  prompt: string;
  correctAnswer: string;
  acceptableAnswers?: string[];
  options?: string[];
  hint?: string;
  index: number;
  isCompleted: boolean;
  onComplete: (id: string, type: string, correct: boolean, score: number, extras?: Partial<ExerciseProgress>) => void;
}

export const FillOrChooseExercise = React.memo(function FillOrChooseExercise({
  exerciseId,
  type,
  prompt,
  correctAnswer,
  acceptableAnswers,
  options,
  hint,
  index,
  isCompleted: _isCompleted,
  onComplete,
}: FillOrChooseExerciseProps) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [showAnswer, setShowAnswer] = useState(false);

  const allCorrectAnswers = [correctAnswer, ...(acceptableAnswers || [])].map((a) => a.toLowerCase().trim());
  const isCorrect = selectedAnswer ? allCorrectAnswers.includes(selectedAnswer.toLowerCase().trim()) : false;

  const handleOptionPress = useCallback((option: string) => {
    if (showAnswer) return;
    setSelectedAnswer(option);
    setShowAnswer(true);
    const correct = allCorrectAnswers.includes(option.toLowerCase().trim());
    onComplete(exerciseId, type, correct, correct ? 100 : 0, { userAnswer: option });
  }, [showAnswer, allCorrectAnswers, onComplete, exerciseId, type]);

  const handleOptionByIndex = useCallback((i: number) => {
    if (options && i < options.length) {
      handleOptionPress(options[i]);
    }
  }, [options, handleOptionPress]);

  // Keyboard shortcuts: number keys select options (web only, no-op on native)
  useNumberKeyShortcuts(options?.length ?? 0, handleOptionByIndex, !showAnswer);

  const handleShowAnswer = useCallback(() => {
    setShowAnswer(true);
    onComplete(exerciseId, type, false, 0, { userAnswer: '' });
  }, [onComplete, exerciseId, type]);

  return (
    <Box
      style={{ backgroundColor: theme.inputBg, borderColor: theme.border }}
      borderWidth={1}
      p="$3"
      rounded="$lg"
    >
      <VStack space="sm">
        <Text
          allowFontScaling={false}
          style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, fontWeight: '600', ...ffs }}
        >
          {t('lessons.detail.exercise')} {index}
        </Text>

        <Text
          allowFontScaling={false}
          style={{ fontSize: FS_SM * fontScale, color: theme.text, fontWeight: '500', ...ffs }}
        >
          {prompt}
        </Text>

        {hint && !showAnswer && (
          <Text
            allowFontScaling={false}
            style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, fontStyle: 'italic', ...ffs }}
          >
            {t('exercises.hint')} {hint}
          </Text>
        )}

        {/* Multiple choice */}
        {type === 'choose_correct' && options && (
          <VStack space="xs">
            {options.map((option, i) => {
              const isSelected = selectedAnswer === option;
              const isOptionCorrect = allCorrectAnswers.includes(option.toLowerCase().trim());
              let optionBg: string = theme.card;
              let optionBorder: string = theme.border;

              if (showAnswer && isSelected) {
                optionBg = isCorrect ? '#D1FAE5' : '#FEE2E2';
                optionBorder = isCorrect ? '#059669' : '#DC2626';
              } else if (showAnswer && isOptionCorrect) {
                optionBg = '#D1FAE5';
                optionBorder = '#059669';
              }

              return (
                <TouchableOpacity
                  key={i}
                  testID={`exercise-option-${index}-${i}`}
                  accessibilityLabel={`Option: ${option}`}
                  accessibilityRole="button"
                  onPress={() => handleOptionPress(option)}
                  disabled={showAnswer}
                >
                  <Box
                    style={{ backgroundColor: optionBg, borderColor: optionBorder }}
                    borderWidth={1}
                    px="$3"
                    py="$2"
                    rounded="$md"
                  >
                    <Text
                      allowFontScaling={false}
                      style={{ fontSize: FS_SM * fontScale, color: theme.text, ...ffs }}
                    >
                      {option}
                    </Text>
                  </Box>
                </TouchableOpacity>
              );
            })}
          </VStack>
        )}

        {/* Show answer for fill_blank and translate */}
        {type !== 'choose_correct' && !showAnswer && (
          <TouchableOpacity
            testID={`exercise-button-show-answer-${index}`}
            accessibilityLabel="Show answer"
            accessibilityRole="button"
            onPress={handleShowAnswer}
          >
            <Box
              style={{ backgroundColor: theme.primarySoft, borderColor: theme.primary }}
              borderWidth={1}
              px="$3"
              py="$2"
              rounded="$md"
              alignItems="center"
            >
              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_SM * fontScale, color: theme.primary, fontWeight: '600', ...ffs }}
              >
                {t('lessons.detail.showAnswer')}
              </Text>
            </Box>
          </TouchableOpacity>
        )}

        {/* Revealed answer */}
        {showAnswer && (
          <Box style={{ backgroundColor: '#D1FAE5' }} p="$2" rounded="$md">
            <Text
              allowFontScaling={false}
              style={{ fontSize: FS_SM * fontScale, color: '#065F46', fontWeight: '600', ...ffs }}
            >
              {'\u2713'} {correctAnswer}
            </Text>
          </Box>
        )}
      </VStack>
    </Box>
  );
});
